/**
 * SignatureSetupModal Component
 * Reusable modal for setting up email signature
 * Used in FormBuilder, EmailSection, and ComposeEmail
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SparklesIcon,
  UserIcon,
  EnvelopeIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';
import leadIntakeService from '../services/LeadIntakeService';
import toast from 'react-hot-toast';

const SIGN_OFFS = [
  { id: 'warmly', label: 'Warmly', preview: 'Warmly,' },
  { id: 'best', label: 'Best', preview: 'Best,' },
  { id: 'cheers', label: 'Cheers', preview: 'Cheers,' },
  { id: 'thanks', label: 'Thanks', preview: 'Thanks,' },
  { id: 'talk_soon', label: 'Talk soon', preview: 'Talk soon,' },
  { id: 'looking_forward', label: 'Looking forward', preview: 'Looking forward to connecting,' }
];

const SignatureSetupModal = ({
  isOpen,
  onClose,
  onComplete,
  userId,
  currentSettings,
  title = "Let's set you up",
  subtitle = "How should we sign your emails?",
  description = "Set up your signature so every email looks professional.",
  showCancel = true,
  cancelText = "Cancel",
  continueText = "Continue"
}) => {
  const [signatureForm, setSignatureForm] = useState({
    name: currentSettings?.signature?.name || '',
    email: currentSettings?.signature?.email || '',
    businessName: currentSettings?.signature?.businessName || '',
    title: currentSettings?.signature?.title || '',
    signOff: currentSettings?.aiSettings?.signOff || 'warmly'
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!signatureForm.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!signatureForm.email.trim()) {
      toast.error('Please enter your email');
      return;
    }

    setSaving(true);
    try {
      const newSettings = {
        ...currentSettings,
        signature: {
          name: signatureForm.name.trim(),
          email: signatureForm.email.trim(),
          businessName: signatureForm.businessName.trim() || signatureForm.name.trim(),
          title: signatureForm.title.trim()
        },
        aiSettings: {
          ...(currentSettings?.aiSettings || {}),
          signOff: signatureForm.signOff
        }
      };

      await leadIntakeService.updateSettings(userId, newSettings);
      toast.success('Signature saved');
      onComplete?.(newSettings);
    } catch (error) {
      toast.error('Failed to save signature');
      console.error('Failed to save signature:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="relative bg-white dark:bg-black rounded-3xl max-w-xl w-full max-h-[90vh] overflow-auto shadow-2xl
            border border-transparent dark:border-white/10"
        >
          <div className="p-8">
            {/* Header */}
            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 rounded-2xl bg-brand/10 flex items-center justify-center">
                <SparklesIcon className="w-6 h-6 text-brand" />
              </div>
              <div>
                <h2 className="text-[24px] font-semibold text-black dark:text-white tracking-tight">
                  {title}
                </h2>
                <p className="text-[14px] text-black/50 dark:text-white/50">
                  {subtitle}
                </p>
              </div>
            </div>

            <p className="text-[14px] text-black/60 dark:text-white/60 mt-4 mb-8">
              {description}
            </p>

            {/* Signature Form */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-[13px] font-medium text-black/60 dark:text-white/60 mb-2">
                    <UserIcon className="w-4 h-4" />
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={signatureForm.name}
                    onChange={(e) => setSignatureForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Sarah Johnson"
                    className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                      border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                      placeholder:text-black/30 dark:placeholder:text-white/30
                      focus:outline-none focus:ring-2 focus:ring-brand/50"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-[13px] font-medium text-black/60 dark:text-white/60 mb-2">
                    <EnvelopeIcon className="w-4 h-4" />
                    Your Email *
                  </label>
                  <input
                    type="email"
                    value={signatureForm.email}
                    onChange={(e) => setSignatureForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="sarah@example.com"
                    className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                      border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                      placeholder:text-black/30 dark:placeholder:text-white/30
                      focus:outline-none focus:ring-2 focus:ring-brand/50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-[13px] font-medium text-black/60 dark:text-white/60 mb-2">
                    <BuildingStorefrontIcon className="w-4 h-4" />
                    Business Name
                  </label>
                  <input
                    type="text"
                    value={signatureForm.businessName}
                    onChange={(e) => setSignatureForm(prev => ({ ...prev, businessName: e.target.value }))}
                    placeholder="Sarah Johnson Photography"
                    className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                      border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                      placeholder:text-black/30 dark:placeholder:text-white/30
                      focus:outline-none focus:ring-2 focus:ring-brand/50"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-[13px] font-medium text-black/60 dark:text-white/60 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={signatureForm.title}
                    onChange={(e) => setSignatureForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Wedding Photographer"
                    className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                      border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                      placeholder:text-black/30 dark:placeholder:text-white/30
                      focus:outline-none focus:ring-2 focus:ring-brand/50"
                  />
                </div>
              </div>

              {/* Sign-off selector */}
              <div>
                <label className="block text-[13px] font-medium text-black/60 dark:text-white/60 mb-3">
                  How do you like to sign off?
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {SIGN_OFFS.map((signOff) => (
                    <button
                      key={signOff.id}
                      onClick={() => setSignatureForm(prev => ({ ...prev, signOff: signOff.id }))}
                      className={`px-4 py-3 rounded-xl text-[14px] font-medium transition-all ${
                        signatureForm.signOff === signOff.id
                          ? 'bg-brand text-white'
                          : 'bg-black/[0.03] dark:bg-white/5 text-black/60 dark:text-white/60 hover:bg-black/[0.06] dark:hover:bg-white/10'
                      }`}
                    >
                      {signOff.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview */}
              {signatureForm.name && (
                <div className="mt-6 p-4 rounded-xl bg-black/[0.02] dark:bg-white/[0.02] border border-black/5 dark:border-white/5">
                  <p className="text-[12px] font-medium text-black/40 dark:text-white/40 mb-2">SIGNATURE PREVIEW</p>
                  <div className="text-[14px] text-black dark:text-white whitespace-pre-line">
                    {SIGN_OFFS.find(s => s.id === signatureForm.signOff)?.preview || 'Warmly,'}
                    {'\n'}{signatureForm.name || 'Your Name'}
                    {signatureForm.title && `\n${signatureForm.title}`}
                    {signatureForm.businessName && signatureForm.businessName !== signatureForm.name && `\n${signatureForm.businessName}`}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-8">
              {showCancel && (
                <button
                  onClick={onClose}
                  className="flex-1 py-3.5 rounded-full text-[14px] font-medium
                    text-black/60 dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                >
                  {cancelText}
                </button>
              )}
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={handleSave}
                disabled={saving || !signatureForm.name || !signatureForm.email}
                className={`${showCancel ? 'flex-1' : 'w-full'} py-3.5 bg-brand text-white rounded-full text-[14px]
                  font-semibold disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {saving ? 'Saving...' : continueText}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SignatureSetupModal;
